import React from 'react'
import { BiBarChartAlt2, BiChevronRight, BiTable } from 'react-icons/bi'
import type { AgentReportCard as AgentReportCardData } from '../agentReportCards'

interface Props {
  card: AgentReportCardData
  active?: boolean
  onView: (card: AgentReportCardData) => void
}

const verb: Record<string, string> = { create: 'Created', update: 'Updated', clone: 'Cloned', get: 'Saved report' }

/**
 * Inline chat card for a report the agent saved. Same shape as the generated-PDF card: a branded cover with the
 * report's title, and a meta row. Reports open in the side panel; dashboard reports open their own page.
 */
const AgentReportCard: React.FC<Props> = ({ card, active, onView }) => {
  const isReport = card.kind === 'report'
  const body = (
    <>
      {/* Branded cover with the report title */}
      <div className="tw-relative tw-h-28 tw-px-4 tw-py-3 tw-overflow-hidden tw-bg-gradient-to-br tw-from-blue-600 tw-to-sky-500 tw-flex tw-flex-col tw-justify-between">
        <div className="tw-text-white tw-text-sm tw-font-semibold tw-leading-snug tw-overflow-hidden tw-pr-6" style={{ maxHeight: '3rem' }}>
          {card.title || 'Report'}
        </div>
        <div className="tw-text-blue-100 tw-text-xs tw-uppercase tw-tracking-wide">
          {isReport ? 'PayHelm Report' : 'PayHelm Dashboard Report'}
        </div>
        <BiBarChartAlt2 className="tw-absolute tw-top-3 tw-right-3 tw-text-white tw-opacity-80" size={18} />
      </div>
      {/* Meta row */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
        <div className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded tw-bg-blue-50 tw-flex-shrink-0">
          <BiTable className="tw-text-blue-600" size={18} />
        </div>
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-text-xs tw-font-medium tw-text-gray-900 tw-truncate">
            {[verb[card.action] || 'Report', card.typeLabel].filter(Boolean).join(' · ')}
          </div>
          <div className="tw-text-xs tw-text-gray-500 tw-truncate">
            {card.details.length ? card.details.join(' · ') : isReport ? 'Click to view' : 'Click to open'}
          </div>
        </div>
        <BiChevronRight className="tw-text-gray-400 group-hover:tw-text-blue-500 tw-flex-shrink-0" size={20} />
      </div>
    </>
  )
  const cls = `tw-group tw-w-64 tw-max-w-full tw-text-left tw-rounded-xl tw-overflow-hidden tw-border tw-bg-white hover:tw-border-blue-300 hover:tw-shadow-md tw-transition tw-cursor-pointer tw-no-underline ${
    active ? 'tw-border-blue-400 tw-shadow-md' : 'tw-border-gray-200'
  }`
  if (!isReport) {
    return (
      <a href={`/custom/${card.id}`} target="_blank" rel="noopener noreferrer" title={`Open ${card.title}`} className={cls}>
        {body}
      </a>
    )
  }
  return (
    <button type="button" onClick={() => onView(card)} title={`View ${card.title}`} className={cls}>
      {body}
    </button>
  )
}

export default AgentReportCard
